/**
 * Hausa catalog (Latin / Boko) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. Native-grade Standard Hausa at a mathematician's register. **Warrant → «Hujja»**
 * (the Arabic-derived Hausa term for proof/justification/warrant — a strong fit). Scientific
 * loanwords (logic, modal, heuristic, postulate, geometry) are used where Hausa standardly
 * borrows. W/M grades stay Latin (`::numbering-system/latn`). CLDR cardinal categories are
 * one / other, so `frameActive` declares exactly those (plus an explicit `=0`). Some abstract
 * coinages are flagged "expert-review suggested" in `Agent9_Batch3_REPORT.md`. Per INV-STABLE-ID
 * a wrong translation is a presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const HA_CATALOG = {
    locale: 'ha',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Bincike',
        [P2.sectionClaims]: "Da'awoyi",
        [P2.sectionIntegrity]: 'Inganci',
        [P2.axisWarrantLabel]: 'Hujja',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Na gwaji',
        [P2.axisEmpiricalValue]: "{kind, select, defn {Ma'ana} interp {Fassara} testable {Mai gwadawa} tested {An gwada} refuted {An ƙaryata} program {Shirin bincike} philos {Na falsafa} other {Ba a sani ba}}",
        [P2.axisModalLabel]: 'Modal',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Matakin ƙarfi {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Mai bincike',
        [P2.stateRefuted]: 'An ƙaryata',
        [P2.stateOutside]: 'Bayan iyaka',
        [P2.verdictConflict]: 'Yana cin karo da {constraints}',
        [P2.verdictOutside]: 'Yana buƙatar {missing}',
        [P2.lossRegisterPlain]: 'An sauƙaƙa zuwa harshe na yau da kullum — an bar wani ɓangare na daidaito.',
        [P2.lossRegisterKid]: 'An sauƙaƙa wa ƙananan masu karatu — an bar bayanai da yawa.',
        [P2.lossProfileDetail]: 'An taƙaita cikakken bayanin Hujja / Na gwaji / Modal a nan.',
        [P2.lossConflictDetail]: 'An taƙaita bayanan rikici a wannan kallon.',
        [P2.laneVerified]: 'An tabbatar',
        [P2.laneUnverified]: 'Ba a tabbatar ba',
        [P2.laneConflict]: 'An tabbatar da rikici',
        [P2.constraintLogic]: 'Lojik',
        [P2.constraintLightBends]: 'Haske yana lankwasa a filin nauyi',
        [P2.constraintParallelPostulate]: 'Postulat na layuka masu daidaituwa',
        [P2.claimScalarGravity]: "Ka'idar nauyi ta sikela",
        [P2.claimNonEuclidean]: 'Geometri wadda ba ta Euclid ba',
        [P2.claimSubstrateSr]: 'Fassarar tushe na alaƙar musamman',
        [P2.edgeExtObservation]: 'Lura daga waje',
        [P2.edgeResourceSample]: 'Albarkatun ƙididdiga da ake rabawa',
        [P2.edgeHeuristicSample]: 'Jagora na heuristic',
        [P2.edgeSharedSample]: 'Tushen da ake rabawa',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Iyakoki',
        [P2.sectionEdges]: 'Gefuna',
        [P2.sectionFrame]: 'Tsari',
        [P2.sectionExposure]: 'Fallasa',
        [P2.statusAdoptedFormal]: 'An karɓa (na hukuma)',
        [P2.statusAdoptedInterpretive]: 'An karɓa (na fassara)',
        [P2.statusSuspended]: 'An dakatar',
        [P2.edgeEffectConstraint]: 'Yana samar da iyaka (zai iya ƙaryatawa)',
        [P2.edgeEffectResource]: 'Albarkatu — babu tasiri kan hukunce-hukunce',
        [P2.edgeEffectHeuristic]: 'Heuristic — babu tasiri kan hukunce-hukunce',
        [P2.edgeEffectShared]: 'Tushen da ake rabawa — babu tasiri kan hukunce-hukunce',
        [P2.frameActive]: '{count, plural, =0 {Babu iyaka mai aiki} one {# iyaka mai aiki} other {# iyakoki masu aiki}}',
        [P2.exposureNone]: 'Babu dogaro na fassara',
        [P2.exposureDep]: 'Ya dogara da {id}',
        [P2.laneTrackMachine]: "Na'ura",
        [P2.laneTrackExpert]: 'Masani',
        [P2.laneTrackCommunity]: "Al'umma",
        [P2.expertAudited]: 'An duba',
        [P2.expertInProgress]: 'Ana ci gaba',
        [P2.expertNotReviewed]: 'Ba a sake dubawa ba',
        [P2.communityFlags]: 'Akwai alamomi',
        [P2.communityNone]: 'Babu alamomi',
        [P2.controlChallenge]: 'Ƙalubalanta',
        [P2.controlActivate]: 'Kunna',
        [P2.controlMakeInterpretive]: 'Mai da na fassara',
        [P2.controlImportEdge]: 'Shigo da gefe',
        [P2.controlPromote]: 'Ɗaga',
        [P2.controlUndo]: 'Janye',
        [P2.controlRedo]: 'Sake yi',
        [P2.controlRegister]: 'Yi rajista',
        [P2.promoteRejected]: 'An ƙi ɗagawa',
        [P2.promoteAccepted]: 'An karɓi ɗagawa',
        [P2.promoteReasonRefutationTerminal]: 'Hujja ba za ta iya warkar da ƙaryatawa ba.',
        [P2.promoteReasonKeystone]: 'Ɗagawa mai mahimmanci yana buƙatar goyon bayan gwaji.',
        [P2.promoteReasonEmpiricalNotFixed]: "Nau'in gwaji bai tabbata ba.",
        [P2.promoteReasonModalNotFixed]: 'Matakin modal bai tabbata ba.',
        [P2.promoteReasonWarrantNotImproved]: 'Matakin hujja bai inganta ba.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=ha.js.map